/**
 * Conversation Store
 * Manages chat history state with Zustand
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '@/services/api'

export interface Project {
  id: string
  name: string
  description?: string
  color?: string
  icon?: string
  is_archived: boolean
  conversation_count: number
  created_at: string
  updated_at?: string
}

export interface ConversationPreview {
  id: string
  title: string
  project_id?: string
  is_pinned: boolean
  last_message_preview?: string
  last_message_at?: string
  message_count: number
}

export interface Message {
  id: string
  conversation_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  tool_calls?: any
  tool_results?: any
  metadata?: any
  created_at: string
}

export interface Conversation extends ConversationPreview {
  summary?: string
  is_archived: boolean
  messages?: Message[]
  created_at: string
  updated_at?: string
}

interface ConversationState {
  // Data
  projects: Project[]
  conversations: ConversationPreview[]
  currentConversation: Conversation | null
  currentProjectId: string | null
  
  // UI State
  isLoadingProjects: boolean
  isLoadingConversations: boolean
  isLoadingMessages: boolean
  searchQuery: string
  sidebarOpen: boolean
  
  // Actions - Projects
  loadProjects: () => Promise<void>
  createProject: (name: string, color?: string) => Promise<Project>
  updateProject: (id: string, data: Partial<Project>) => Promise<void>
  deleteProject: (id: string) => Promise<void>
  setCurrentProject: (projectId: string | null) => void
  
  // Actions - Conversations
  loadConversations: (projectId?: string | null, search?: string) => Promise<void>
  createConversation: (projectId?: string, title?: string) => Promise<Conversation>
  updateConversation: (id: string, data: Partial<Conversation>) => Promise<void>
  deleteConversation: (id: string) => Promise<void>
  setCurrentConversation: (conversationId: string | null) => Promise<void>
  
  // Actions - Messages
  addMessage: (conversationId: string, message: Omit<Message, 'id' | 'conversation_id' | 'created_at'>) => Promise<Message>
  
  // Actions - UI
  setSearchQuery: (query: string) => void
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  
  // Actions - Sync
  syncCurrentConversation: () => Promise<void>
}

export const useConversationStore = create<ConversationState>()(
  persist(
    (set, get) => ({
      // Initial State
      projects: [],
      conversations: [],
      currentConversation: null,
      currentProjectId: null,
      isLoadingProjects: false,
      isLoadingConversations: false,
      isLoadingMessages: false,
      searchQuery: '',
      sidebarOpen: true,
      
      // Projects
      loadProjects: async () => {
        set({ isLoadingProjects: true })
        try {
          const response = await api.get('/conversations/projects')
          set({ projects: response.data.projects })
        } catch (error) {
          console.error('Failed to load projects:', error)
        } finally {
          set({ isLoadingProjects: false })
        }
      },
      
      createProject: async (name: string, color?: string) => {
        const response = await api.post('/conversations/projects', { name, color })
        const project = response.data
        set(state => ({ projects: [project, ...state.projects] }))
        return project
      },
      
      updateProject: async (id: string, data: Partial<Project>) => {
        await api.patch(`/conversations/projects/${id}`, data)
        set(state => ({
          projects: state.projects.map(p => p.id === id ? { ...p, ...data } : p)
        }))
      },
      
      deleteProject: async (id: string) => {
        await api.delete(`/conversations/projects/${id}`)
        set(state => ({
          projects: state.projects.filter(p => p.id !== id),
          currentProjectId: state.currentProjectId === id ? null : state.currentProjectId
        }))
      },
      
      setCurrentProject: (projectId: string | null) => {
        set({ currentProjectId: projectId })
        get().loadConversations(projectId)
      },
      
      // Conversations
      loadConversations: async (projectId?: string | null, search?: string) => {
        set({ isLoadingConversations: true })
        try {
          const params: any = {}
          if (projectId) params.project_id = projectId
          if (search) params.search = search
          
          const response = await api.get('/conversations', { params })
          set({ conversations: response.data.conversations })
        } catch (error) {
          console.error('Failed to load conversations:', error)
        } finally {
          set({ isLoadingConversations: false })
        }
      },
      
      createConversation: async (projectId?: string, title?: string) => {
        const response = await api.post('/conversations', { 
          project_id: projectId || get().currentProjectId,
          title 
        })
        const conversation = response.data
        set(state => ({
          conversations: [conversation, ...state.conversations],
          currentConversation: { ...conversation, messages: [] }
        }))
        return conversation
      },
      
      updateConversation: async (id: string, data: Partial<Conversation>) => {
        await api.patch(`/conversations/${id}`, data)
        set(state => ({
          conversations: state.conversations.map(c => 
            c.id === id ? { ...c, ...data } : c
          ),
          currentConversation: state.currentConversation?.id === id 
            ? { ...state.currentConversation, ...data }
            : state.currentConversation
        }))
      },
      
      deleteConversation: async (id: string) => {
        await api.delete(`/conversations/${id}`)
        set(state => ({
          conversations: state.conversations.filter(c => c.id !== id),
          currentConversation: state.currentConversation?.id === id 
            ? null 
            : state.currentConversation
        }))
      },
      
      setCurrentConversation: async (conversationId: string | null) => {
        if (!conversationId) {
          set({ currentConversation: null })
          return
        }
        
        set({ isLoadingMessages: true })
        try {
          const response = await api.get(`/conversations/${conversationId}`)
          set({ currentConversation: response.data })
        } catch (error) {
          console.error('Failed to load conversation:', error)
        } finally {
          set({ isLoadingMessages: false })
        }
      },
      
      // Messages
      addMessage: async (conversationId: string, message) => {
        const response = await api.post(`/conversations/${conversationId}/messages`, message)
        const newMessage = response.data
        
        set(state => {
          if (state.currentConversation?.id !== conversationId) return state
          
          const messages = state.currentConversation.messages || []
          return {
            currentConversation: {
              ...state.currentConversation,
              messages: [...messages, newMessage],
              last_message_at: newMessage.created_at,
            },
            conversations: state.conversations.map(c => 
              c.id === conversationId 
                ? { 
                    ...c, 
                    last_message_preview: newMessage.content.substring(0, 100),
                    last_message_at: newMessage.created_at,
                    message_count: c.message_count + 1,
                  }
                : c
            )
          }
        })
        
        return newMessage
      },
      
      // UI
      setSearchQuery: (query: string) => {
        set({ searchQuery: query })
        if (query) {
          get().loadConversations(get().currentProjectId, query)
        } else {
          get().loadConversations(get().currentProjectId)
        }
      },
      
      toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
      
      // Sync
      syncCurrentConversation: async () => {
        const { currentConversation } = get()
        if (currentConversation) {
          await get().setCurrentConversation(currentConversation.id)
        }
      },
    }),
    {
      name: 'niki-conversations',
      partialize: (state) => ({
        currentProjectId: state.currentProjectId,
        sidebarOpen: state.sidebarOpen,
      }),
    }
  )
)
